import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import AuthField from "@/components/shared/AuthField";
import { friendlyError } from "@/lib/errors";
import { MailCheck } from "lucide-react";
import { toast } from "sonner";

const RESEND_SECONDS = 60;

// Esqueci a senha: pede o e-mail, backend responde 202 sempre (sem enumeração
// de contas) e enviamos a mesma tela "veja sua caixa de entrada" do link mágico.
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const startResendCountdown = () => {
    setResendIn(RESEND_SECONDS);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendIn((s) => {
        if (s <= 1) { clearInterval(timerRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
      startResendCountdown();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-brand-bg flex items-center justify-center px-6 py-12 relative overflow-hidden"
      data-testid="forgot-password-page"
    >
      <div className="absolute inset-0 starfield opacity-90 pointer-events-none" />
      <div className="absolute inset-0 bloom-top pointer-events-none" />

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-10">
          <Link to="/"><BrandLogo size="lg" /></Link>
        </div>
        <div className="glass-card rounded-3xl p-8 sm:p-10">
          {sent ? (
            <div className="text-center" data-testid="forgot-sent">
              <div className="w-14 h-14 rounded-full bg-brand-rose/12 border border-brand-rose/40 flex items-center justify-center mx-auto mb-5">
                <MailCheck className="w-6 h-6 text-brand-rose" />
              </div>
              <h1 className="font-display text-3xl text-white mb-2 tracking-tight">Verifique seu e-mail ✦</h1>
              <p className="text-[14px] text-brand-ink2 mb-1">
                Se existir uma conta para{" "}
                <span className="text-brand-ink">{email}</span>, enviamos um link para criar uma nova senha.
              </p>
              <p className="text-[12.5px] text-brand-ink3 mb-7">
                Não chegou? Veja a caixa de spam. O link expira em 15 minutos.
              </p>
              <button
                type="button"
                data-testid="forgot-resend"
                onClick={onSubmit}
                disabled={resendIn > 0 || loading}
                className="w-full py-3.5 rounded-full bg-brand-elevated border border-brand-line text-brand-ink text-[14px] font-ui hover:border-brand-rose transition-colors disabled:opacity-50 min-h-[44px]"
              >
                {resendIn > 0 ? `Reenviar em ${resendIn}s` : loading ? "Reenviando..." : "Reenviar link"}
              </button>
              <Link
                to="/login"
                data-testid="forgot-back-login"
                className="inline-block mt-3 text-[13px] text-brand-ink2 hover:text-brand-rose font-ui transition-colors"
              >
                Voltar para o login
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-display text-3xl text-white mb-2 tracking-tight">Esqueceu a senha?</h1>
              <p className="text-[14px] text-brand-ink2 mb-6">
                Sem drama. Diga seu e-mail e enviamos um link para criar uma nova.
              </p>
              <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-form">
                <AuthField
                  label="E-mail" type="email" name="email" value={email} onChange={setEmail}
                  testid="forgot-email" autoComplete="email" required
                />
                <button
                  type="submit"
                  data-testid="forgot-submit"
                  disabled={loading}
                  className="pill-pink w-full py-3.5 text-[14px] tracking-wide"
                >
                  {loading ? "Enviando..." : "Enviar link de redefinição"}
                </button>
              </form>
              <p className="text-[13px] text-brand-ink2 mt-7 text-center font-ui">
                Lembrou a senha?{" "}
                <Link to="/login" className="text-brand-rose hover:text-brand-roseHover" data-testid="link-login">
                  Entrar
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
